import { api } from '../../api/axios';
import type { AdvancedDashboardData } from './dashboardAdvancedTypes';
import type {
  ConversionTypeCount,
  CreditRiskClient,
  DebtorData,
  TopWalletData,
  WalletTagData,
  InventoryDepotData,
  KPIData,
  MovementTypeCount,
  PaymentModeData,
  PaymentTypeData,
  ProjectedProfitRow,
  RecentTransaction,
  SalesByAgentData,
  SalesByCategoryData,
  SalesByDepotData,
  SalesByFamilyData,
  SalesBySectorData,
  SalesDataPoint,
  PaymentsByDepotData,
  PaymentsByUserData,
  SalesProfitRow,
  SupplierData,
  SupplierBalanceData,
  OverdueSupplyData,
  TopItemData,
} from './DashboardContext';
import type { DashboardCoreSection } from './dashboardSections';

export interface DashboardResponse {
  kpi: KPIData;
  salesTrend: SalesDataPoint[];
  salesProfitBreakdown: SalesProfitRow[];
  projectedProfitBreakdown: ProjectedProfitRow[];
  paymentModes: PaymentModeData[];
  paymentTypes: PaymentTypeData[];
  topDebtors: DebtorData[];
  topAgents: DebtorData[];
  inventoryByDepot: InventoryDepotData[];
  recentTransactions: RecentTransaction[];
  movementCounts: MovementTypeCount[];
  conversionCounts: ConversionTypeCount[];
  salesByFamily: SalesByFamilyData[];
  salesByCategory: SalesByCategoryData[];
  salesByDepot: SalesByDepotData[];
  salesBySector: SalesBySectorData[];
  salesByAgent: SalesByAgentData[];
  topItems: TopItemData[];
  paymentsByUser: PaymentsByUserData[];
  paymentsByDepot: PaymentsByDepotData[];
  supplierData: SupplierData[];
  supplierPaymentModes: PaymentModeData[];
  topSupplierBalances: SupplierBalanceData[];
  overdueSupplies: OverdueSupplyData[];
  creditRiskClients: CreditRiskClient[];
  topWallets: TopWalletData[];
  walletByTag: WalletTagData[];
  advanced?: AdvancedDashboardData;
}

/** Partial dashboard payload returned by a single section endpoint. */
export type DashboardSectionResponse = Partial<
  Omit<DashboardResponse, 'kpi' | 'advanced'>
> & {
  kpi?: Partial<KPIData>;
};

export interface DashboardAdvancedSectionResponse {
  advanced: AdvancedDashboardData;
}

const sectionParams = (From: string, To: string, extra?: Record<string, string | number>) => ({
  params: { From, To, ...extra },
});

export const dashboardApi = {
  getSection: (
    section: DashboardCoreSection,
    From: string,
    To: string,
    extra?: Record<string, string | number>,
  ) =>
    api.get<DashboardSectionResponse>(
      `/dashboard/sections/${section}`,
      sectionParams(From, To, extra),
    ),

  getAdvancedSection: (From: string, To: string) =>
    api.get<DashboardAdvancedSectionResponse>(
      '/dashboard/sections/advanced',
      sectionParams(From, To),
    ),

  /** Monolithic fallback — still supported by the backend. */
  getDashboard: (From: string, To: string, advanced = false) =>
    api.get<DashboardResponse>('/dashboard', {
      params: { From, To, ...(advanced ? { advanced: 'true' } : {}) },
    }),
};